import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@herit-fe/heritage-ui';
import { DataTable } from './data-table';
import type { GroupedColumn } from './grouped-table';
import { useScrollTakeover, useStickyTable } from './use-sticky-table';
import { useTableVirtualRows } from './use-table-virtual-rows';

export interface VirtualGroupedTableProps<T> {
  /** 선두 고정 컬럼(rowSpan 2) — 예: 시간대. */
  leadHeader: string;
  leadRender: (row: T, index: number) => ReactNode;
  /** 그룹(2단 헤더) 컬럼들. */
  groups: GroupedColumn[];
  data: T[];
  rowKey: (row: T, index: number) => string | number;
  /** 그룹 sub-cell 값. (row, 그룹 index, subKey). */
  cell: (row: T, groupIndex: number, subKey: string) => ReactNode;
  /** 고정높이 세로 스크롤 + 2단 헤더 sticky + 선두열 sticky-left — `GroupedTable` 과 동일. */
  scroll?: boolean;
  /**
   * 세로 스크롤 높이 상한(`scroll` 일 때만). 반드시 **바운드 값**(px·rem·`vh` 등) — `'auto'`/미상한이면 뷰포트가
   * 스크롤 컨테이너가 안 돼 **2단 헤더 sticky 가 무력화**된다. 기본 `'20rem'`.
   */
  maxHeight?: number | string;
  /** 행 높이 추정치(px) — 셀을 크게 그리는 표가 올려 준다. */
  estimateRowHeight?: number;
  /** 뷰포트 밖 선렌더 행 수. */
  overscan?: number;
  /**
   * 모든 행 높이가 같은 표임을 선언 — 행별 실측을 끄고 첫 행 1회 실측으로 대신한다(스크롤 비용 감소).
   * 셀이 전부 한 줄일 때만 켠다 — 문장이 들어가 줄이 늘 수 있는 표에 켜면 스크롤바가 어긋난다.
   */
  uniformRowHeight?: boolean;
  /** 스크롤 엘리먼트 직접 지정(선택) — 자동 탐색이 못 잡는 배치에서만. */
  getScrollElement?: () => HTMLElement | null;
  /**
   * **2단 헤더를 화면 상단에 고정**한다. `scroll` 과 함께 쓸 것(끄면 무시).
   *
   * 켜면 `maxHeight` 를 덮어쓴다 — 선택이 아니라 전제다(근거는 `useStickyTable`). sticky 배선 자체는 이 표가
   * 이미 갖고 있고(1단 `top-0` + 2단 측정 오프셋), 세로 상한이 없어 **발동만 못 하던** 상태였다.
   */
  stickyHeader?: boolean;
}

/**
 * `GroupedTable` 의 **가상화 판** — 뷰포트에 보이는 행만 DOM 에 올린다(`useTableVirtualRows`).
 * 헤더 구조(2단 sticky·선두열 sticky-left)·컬럼 계약(`GroupedColumn`)은 `GroupedTable` 과 동일하므로
 * **교체가 곧 적용**이다.
 *
 * MPPT·스트링처럼 **행(시각) × 열(채널 × 측정항목)** 이 둘 다 열려 있는 표용 — 스트링은 채널이 12~24개라
 * 열만으로도 수십이고, 거기에 하루치 시각이 곱해지면 셀이 수만 개가 된다. 열은 못 줄이니 행을 가상화한다.
 *
 * ⚠️ 열 가상화는 하지 않는다 — 가로는 `DataTable` 이 `ScrollArea` 로 흡수하고, 2단 헤더 colSpan·선두열 sticky 가
 * 열 인덱스에 얽혀 있어 잘라내면 헤더 정합이 깨진다. 열이 문제가 되면 그때 별도로 다룬다.
 */
export function VirtualGroupedTable<T>({
  leadHeader,
  leadRender,
  groups,
  data,
  rowKey,
  cell,
  scroll = false,
  maxHeight = 'auto',
  estimateRowHeight,
  overscan,
  uniformRowHeight,
  getScrollElement,
  stickyHeader = false,
}: VirtualGroupedTableProps<T>) {
  const { boundedHeight } = useStickyTable(stickyHeader, maxHeight);
  /**
   * 선두열(시각) 가로 고정 — **좌표·레이어·경계선을 전부 인라인으로** 준다.
   *
   * 유틸 클래스로 주면 `DataTable` 이 헤더에 거는 자손 셀렉터(`[&_thead_th]:z-20`·`:bg-*`·`:shadow-*`, 특이도
   * (0,1,2))에 진다 — 실제로 종전 `bg-muted`·`z-30` 이 그 싸움에서 밀리고 있었다. 인라인은 특이도 밖이라 확실하다.
   *
   * ⚠️ 헤더 쪽 `boxShadow` 는 `DataTable` 의 **하단 구분선까지 함께** 적어야 한다 — 인라인이 그 클래스를 통째로
   * 덮으므로, 우측선만 쓰면 2단 헤더 밑줄이 이 칸에서만 끊긴다.
   */
  const leadHeadStyle = scroll
    ? {
        left: 0,
        zIndex: 30,
        boxShadow: 'inset 0 -1px 0 var(--border), inset -1px 0 0 var(--border)',
      }
    : undefined;
  const leadCellStyle = scroll
    ? { left: 0, zIndex: 10, boxShadow: 'inset -1px 0 0 var(--border)' }
    : undefined;
  // 2단 sticky 오프셋 — 1단(그룹) 행 높이를 측정해 2단 `top`(inline, DataTable 의 top-0 클래스를 이김)으로 준다.
  const groupRowRef = useRef<HTMLTableRowElement>(null);
  const [subHeaderTop, setSubHeaderTop] = useState(0);
  useLayoutEffect(() => {
    if (!scroll) return;
    const el = groupRowRef.current;
    if (!el) return;
    const measure = () => setSubHeaderTop(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scroll]);

  const { bodyRef, indexes, padTop, padBottom, probeHeight, measureRef } = useTableVirtualRows({
    count: data.length,
    estimateRowHeight,
    overscan,
    uniformRowHeight,
    getScrollElement,
    // 상한이 걸린 표는 스크롤러가 자기 뷰포트로 확정 — 인계가 `overflow` 를 토글해도 안 흔들린다.
    ownViewport: stickyHeader,
  });
  // 표가 화면에 다 들어오기 전엔 내부 스크롤을 죽인다(어중간한 인계 방지).
  useScrollTakeover(bodyRef, stickyHeader);

  // 스페이서 `tr` 의 열 수 — 선두열 + 전 그룹의 하위 열. colSpan 을 안 맞추면 열 폭 계산이 흔들린다.
  const totalColumns = 1 + groups.reduce((sum, group) => sum + group.subColumns.length, 0);

  return (
    <DataTable scroll={scroll} grid maxHeight={boundedHeight}>
      <TableHeader>
        {/* 1단 — 시간(2행 병합) + 그룹(하위 수만큼 병합). scroll 시 행 높이를 측정해 2단 sticky top 오프셋으로 쓴다. */}
        <TableRow ref={scroll ? groupRowRef : undefined}>
          <TableHead
            rowSpan={2}
            className={scroll ? 'sticky bg-card align-middle' : 'align-middle'}
            style={leadHeadStyle}
          >
            {leadHeader}
          </TableHead>
          {groups.map((group) => (
            <TableHead key={group.key} colSpan={group.subColumns.length} className="text-center">
              {group.header}
            </TableHead>
          ))}
        </TableRow>
        {/* 2단 — 하위 헤더. scroll 시 sticky top = 측정한 1단 높이(inline)로 1단 바로 아래 고정. */}
        <TableRow>
          {groups.flatMap((group) =>
            group.subColumns.map((sub) => (
              <TableHead
                key={`${group.key}-${sub.key}`}
                className="whitespace-nowrap text-right"
                style={scroll ? { top: subHeaderTop } : undefined}
              >
                {sub.header}
              </TableHead>
            )),
          )}
        </TableRow>
      </TableHeader>
      <TableBody ref={bodyRef}>
        {padTop > 0 ? (
          <tr style={{ height: padTop }} aria-hidden>
            <td colSpan={totalColumns} />
          </tr>
        ) : null}
        {indexes.map((ri) => {
          const row = data[ri]!;
          return (
            <TableRow key={rowKey(row, ri)} ref={measureRef} data-index={ri}>
              <TableCell
                className={
                  scroll
                    ? 'sticky whitespace-nowrap bg-card font-medium'
                    : 'whitespace-nowrap font-medium'
                }
                style={leadCellStyle}
              >
                {leadRender(row, ri)}
              </TableCell>
              {groups.flatMap((group, gi) =>
                group.subColumns.map((sub) => (
                  <TableCell
                    key={`${group.key}-${sub.key}`}
                    className="whitespace-nowrap text-right tabular-nums"
                  >
                    {cell(row, gi, sub.key)}
                  </TableCell>
                )),
              )}
            </TableRow>
          );
        })}
        {padBottom > 0 ? (
          <tr style={{ height: padBottom }} aria-hidden>
            <td colSpan={totalColumns} />
          </tr>
        ) : null}
        {/* 스크롤러 판정용 프로브 — **폴백에서만** 선다. 행이 하나도 없는 상태로 「넘친다」를 만들어
            `verticalScrollParentOf` 가 진짜 스크롤러를 집게 한다(훅 폴백 분기 주석). 한 프레임짜리다. */}
        {probeHeight > 0 ? (
          <tr style={{ height: probeHeight }} aria-hidden>
            <td colSpan={totalColumns} />
          </tr>
        ) : null}
      </TableBody>
    </DataTable>
  );
}
